import axios from 'axios';
import { getToken, isTokenExpired, logout, refreshAccessToken } from './authService';

// Define base API URL
const BASE_URL = process.env.REACT_APP_BASE_URL + "/";

// Define allowed public URLs (match your middleware)
const allowedUrls = [
  "/ap/admin/login",
  "/ap/admin/forgot-password",
  "/ap/admin/verify",
  "/ap/admin/update-password",
  "/ap/admin/send-reset-code",
  "/ma/user/register",
  "/ma/user/login",
  "/ma/user/social-login",
  "/ma/user/forgot-password",
  "/ma/user/verify",
  "/mp/user/register",
  "/mp/user/login",
  "/mp/user/forgot-password",
  "/mp/user/reset-password",
  "/mp/subscription/create",
];

// Create Axios instance
export const axiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 45000, // Increased timeout
  headers: { 'Content-Type': 'application/json' }
});

// Request Interceptor: Attach Token (if needed)
axiosInstance.interceptors.request.use(
  function (config) {
    const token = getToken();

    // Attach token only if it's not in the public routes
    if (token && !allowedUrls.includes(config.url)) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

// Response Interceptor: auto-refresh on expiry, force logout on account disable
axiosInstance.interceptors.response.use(
  function (response) {
    return response;
  },
  async function (error) {
    const original = error?.config || {};
    const status = error?.response?.status;
    const isPublic = allowedUrls.includes(original.url);

    // Account deleted/suspended → end the session immediately (the backend tags
    // these 403s so we don't log out on an incidental authorization 403).
    if (status === 403 && error?.response?.data?.code === "ACCOUNT_DISABLED") {
      logout();
      return Promise.reject(error);
    }

    // Access token expired → single-flight refresh, then retry the request once.
    // `getToken()` must be present: a 401 with NO token means there was never a
    // session on this page (e.g. an authenticated call fired from /login) — there
    // is nothing to refresh and nothing to log out of, so just surface the error.
    // Calling logout() there caused an infinite reload loop on the login page.
    if (status === 401 && !isPublic && !original._retry && getToken() && isTokenExpired()) {
      original._retry = true;
      const newToken = await refreshAccessToken();
      if (newToken) {
        // request interceptor re-attaches the freshly stored token
        return axiosInstance(original);
      }
      logout();
      return Promise.reject(error);
    }

    // Any other 401 (no token, still-valid token, refresh endpoint itself) is an
    // authorization problem for the caller to handle — never nuke the session.
    return Promise.reject(error);
  }
);
